/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTitle } from 'react-use';
import { Common } from '@thecvlb/design-system';

import {
  useLoginMutation,
  useSignInWithAppleMutation,
  useSignInWithGoogleMutation
} from 'services/auth/auth';
import { IdpAuthSteps, SignInWithProviderResProps } from 'services/auth/auth.types';

import { setUserState } from 'store/signup/signupSlice';
import { setUser } from 'store/user/userSlice';

import SignIn from 'layouts/SignIn';
import ContinueWithEmailForm from 'pages/Login/components/ContinueWithEmailForm';

import FailedLogin from 'modals/FailedLogin';
import ButtonAuthWith from 'shared/ButtonAuthWith';
import EmailInput from 'shared/form/EmailInput';
import Loader from 'shared/Loader';
import { notifyError, notifyToast } from 'shared/Toast/Toast';
import LoginPreview from 'widgets/LoginPreview';

import { LINK_TO_MEMBERSHIP } from 'constants/externalLinks';
import { useAppDispatch } from 'hooks';
import useAnalytics from 'hooks/useAnalytics';
import { useAppleLogin } from 'hooks/useAppleLogin';
import useClearSignup from 'hooks/useClearSignup';
import useGoogleLogin from 'hooks/useGoogleLogin';
import useWidth from 'hooks/useWidth';
import { FlowTypes, PathName, PlanCodes, PlanTypes, SignUpSteps } from 'utils/enums';
import { handleRequestCatch } from 'utils/helpers';

import { UserProfile } from 'models/user.types';

import { FormInputs } from './login.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const profitwell: any;

const Login = () => {
  useTitle('LifeMD - Login');
  const logEvent = useAnalytics();
  const { isMobile } = useWidth();
  const clearSignupStore = useClearSignup();
  const [searchParams] = useSearchParams();
  const showForm = ['all', 'email'].includes(searchParams.get('with') ?? '');
  const predefinedEmail = searchParams.get('email') ?? '';

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showContinueWithEmail, setShowContinueWithEmail] = useState(false);
  const [failedLogin, setFailedLogin] = useState<'google' | 'apple' | ''>('');
  const [loginWithEmail, { isLoading: isLoadingLogin }] = useLoginMutation();
  const [authWithGoogle, { isLoading: isLoadingGoogleSignIn }] = useSignInWithGoogleMutation();
  const [authWithApple, { isLoading: isLoadingAppleSignIn }] = useSignInWithAppleMutation();
  const isLoading = isLoadingGoogleSignIn || isLoadingAppleSignIn;

  const { handleSubmit, control, formState, setValue } = useForm<FormInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { field } = useController({
    control,
    defaultValue: '',
    name: 'password',
    rules: {
      required: {
        message: 'Password is required',
        value: true
      }
    }
  });

  const handleLoginThen = (data: UserProfile) => {
    if (data.status === 'pending' && data.isInsuranceSignup) {
      notifyToast('Your account is not yet activated. Please complete your signup');
      navigate(
        `${PathName.SignUp}/${PlanTypes.WeightManagement}/${FlowTypes.WeightManagementFlowInsurance}`
      );
    } else if (data.status === 'pending' && data.hasSubscription) {
      dispatch(setUserState(data));
      navigate(
        `${PathName.SignUp}/${PlanTypes.UnlimitedPlan}/${FlowTypes.ShortFlow}/${SignUpSteps.CreateNewUser}?status=pending`
      );
    } else {
      dispatch(setUser(data));
      if (sessionStorage.getItem('suggest-wm-after-login') === 'true') {
        if (data.activePlanCode !== PlanCodes.WeightManagementMembership) {
          setTimeout(() => navigate(PathName.MigrateToWM));
        } else {
          sessionStorage.removeItem('suggest-wm-after-login');
        }
      }
    }
    profitwell('user_email', data.email);
  };

  const handleSignInWithProviderThen = (
    data: SignInWithProviderResProps,
    provider: 'google' | 'apple'
  ) => {
    const idpAuthStep = data?.data?.step;
    const idpUser = data?.data?.user;
    if (
      idpAuthStep === IdpAuthSteps.CONFIRM_SIGNIN ||
      idpAuthStep === IdpAuthSteps.CONFIRM_SIGNUP ||
      idpAuthStep === IdpAuthSteps.SIGNIN_UNAVAILABLE_USE_ANOTHER_IDP ||
      !idpUser
    ) {
      return setFailedLogin(provider);
    }
    if (
      idpAuthStep === IdpAuthSteps.SIGNIN_SUCCESS ||
      idpAuthStep === IdpAuthSteps.SIGNUP_SUCCESS
    ) {
      handleLoginThen(idpUser);
    }
  };

  const loginWithGoogle = useGoogleLogin({
    flow: 'auth-code',
    onError: (error) => error?.error_description && notifyError(error.error_description),
    onSuccess: (response) => {
      authWithGoogle({
        authorizationCode: response.code
      })
        .unwrap()
        .then((res) => handleSignInWithProviderThen(res, 'google'))
        .catch(handleRequestCatch);
    }
  });

  const loginWithApple = useAppleLogin({
    clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
    // @ts-ignore
    // eslint-disable-next-line no-console
    onError: (error) => error?.error && console.error(error?.error),
    onSuccess: (response) => {
      // eslint-disable-next-line no-console
      if (!response?.authorization?.code) return console.error('No auth code', response);
      authWithApple({
        authorizationCode: response?.authorization?.code
      })
        .unwrap()
        .then((res) => handleSignInWithProviderThen(res, 'apple'))
        .catch(handleRequestCatch);
    },
    redirectURI: import.meta.env.VITE_APPLE_REDIREDCT_URI
  });

  const onLoginFormSubmit = (formData: FormInputs) => {
    logEvent('login_sign_in_btn_click');
    loginWithEmail(formData)
      .unwrap()
      .then((res) => handleLoginThen(res.data))
      .catch(handleRequestCatch);
  };

  const handleCloseFailedLogin = (tryDifferentEmail?: boolean) => {
    if (tryDifferentEmail) {
      failedLogin === 'google' ? loginWithGoogle() : loginWithApple();
    }
    setFailedLogin('');
  };

  const handleClickForgotPassword = () => {
    logEvent('login_forgot_pass_btn_click');
  };

  const handleClickLoginWithApple = () => {
    logEvent('social_apple_btn_click');
    loginWithApple();
  };

  const handleClickLoginWithGoogle = () => {
    logEvent('social_google_btn_click');
    loginWithGoogle();
  };

  const handleClickLoginWithEmail = () => {
    logEvent('social_email_btn_click');
    navigate({ pathname: PathName.Login, search: '?new=false&with=email' });
  };

  useEffect(() => {
    if (formState.isSubmitted) {
      buttonRef?.current?.focus();
    }
  }, [formState.isSubmitted]);

  useEffect(() => {
    setShowContinueWithEmail(searchParams.get('with') === 'email');
  }, [searchParams]);

  useEffect(() => {
    if (predefinedEmail) {
      setValue('email', predefinedEmail);
    }
    clearSignupStore();
  }, []);

  if (!showForm && isMobile) {
    return <LoginPreview />;
  }

  return (
    <SignIn>
      <FailedLogin isOpen={!!failedLogin} onClose={handleCloseFailedLogin} />
      {!isMobile && <Common.Logo className="mx-auto w-auto" name="logo-new" />}
      <div className="md:w-[300px]">
        <Loader isVisible={isLoading} />
        {isMobile ? (
          <div className="flex flex-col items-center justify-center gap-8">
            {showContinueWithEmail ? (
              <ContinueWithEmailForm
                predefinedEmail={predefinedEmail}
                onSubmit={onLoginFormSubmit}
              />
            ) : (
              <>
                <h1 className="text-center text-mLg font-bold text-white">
                  Welcome!
                  <br />
                  Let’s get started!
                </h1>
                <div className="relative w-full space-y-3">
                  <ButtonAuthWith
                    disabled={isLoading}
                    provider="apple"
                    onClick={handleClickLoginWithApple}
                  />
                  <ButtonAuthWith
                    disabled={isLoading}
                    provider="google"
                    onClick={handleClickLoginWithGoogle}
                  />
                  <ButtonAuthWith
                    disabled={isLoading}
                    provider="email"
                    onClick={handleClickLoginWithEmail}
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="my-8 space-y-2">
              <ButtonAuthWith
                disabled={isLoading}
                provider="google"
                onClick={handleClickLoginWithGoogle}
              />
              <ButtonAuthWith
                disabled={isLoading}
                provider="apple"
                onClick={handleClickLoginWithApple}
              />
            </div>
            <div className="mb-8 flex items-center justify-between">
              <hr className="w-full text-gray-200" />
              <span className="mb-1 px-2 text-gray-500">or</span>
              <hr className="w-full text-gray-200" />
            </div>
            <form role="form" onSubmit={handleSubmit(onLoginFormSubmit)}>
              <div className="mb-3 mt-8 flex flex-col gap-4">
                <EmailInput control={control} dataTestId="email_field" name="email" />
                <Common.Input
                  dataTestId="password_field"
                  {...field}
                  errors={formState.errors[field.name]}
                  helper={formState.errors[field.name]?.message?.toString()}
                  placeholder="Password"
                  type="password"
                />
              </div>
              <Common.Button
                className="w-full justify-center bg-secondary md:bg-primary"
                color="blue"
                dataTestId="sign_in_btn"
                disabled={isLoadingLogin || !formState.isValid}
                isLoading={isLoadingLogin}
                ref={buttonRef}
                size="md"
              >
                Sign in
              </Common.Button>
            </form>
            <Link
              className="mx-auto mt-3 block w-fit text-mSm font-semibold text-white md:text-sm md:text-gray"
              data-testid="forgot_pass_btn"
              to={PathName.ForgotPassword}
              onClick={handleClickForgotPassword}
            >
              Forgot your password?
            </Link>
            <div className="mt-8 hidden items-center justify-center gap-2 md:flex">
              <span className="w-fit text-base font-bold text-primary">Not a member?</span>
              <Common.Button
                color="blue-alt"
                data-testid="register_button"
                onClick={() => window.open(LINK_TO_MEMBERSHIP, '_blank')}
              >
                Register
              </Common.Button>
            </div>
          </>
        )}
      </div>
    </SignIn>
  );
};

export default Login;
